<template>
  <div class="mvp-tab-panel">
    <BaseTable
      :items="orders"
      :columns="columns"
      :loading="loading"
      :title="`Marked Orders (${pagination.total})`"
      accent="linear-gradient(180deg, #0ea5e9, #0369a1)"
      :show-count="false"
      :sortable="false"
      :striped="true"
      :hover="true"
      :sticky-header="false"
      empty-text="No marked orders in this date range."
    >
      <template #cell-sl_no="{ index }">
        {{ slNoFor(index) }}
      </template>

      <template #cell-products="{ value }">
        <ProductsCell :value="value" />
      </template>

      <template #cell-order_number="{ value }">
        <span class="mvp-order-no">{{ value || '—' }}</span>
      </template>

      <template #cell-created_at="{ value }">
        <span class="mvp-date">{{ formatDate(value) }}</span>
      </template>

      <template #cell-care_of_name="{ value }">
        <span class="mvp-care-of-pill">{{ value || '—' }}</span>
      </template>

      <template #cell-actions="{ row, index }">
        <CButton
          size="sm"
          class="mvp-row-btn mvp-row-btn--change"
          @click="$emit('select', { order: row, slNo: slNoFor(index) })"
        >
          Change
        </CButton>
      </template>
    </BaseTable>

    <div v-if="pagination.total > 0" class="mvp-pagination-bar">
      <span class="mvp-pagination-info">{{ rangeLabel }}</span>
      <AppPager
        :current-page="Number(pagination.current_page) || 1"
        :last-page="Number(pagination.last_page) || 1"
        @change="onPageChange"
      />
    </div>
  </div>
</template>

<script>
import ProductsCell from './ProductsCell.vue'
import AppPager from '@/components/AppPager.vue'

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export default {
  name: 'MarkedOrdersTab',
  components: { ProductsCell, AppPager },
  props: {
    orders: { type: Array, default: () => [] },
    pagination: {
      type: Object,
      default: () => ({ current_page: 1, per_page: 25, total: 0, last_page: 1 })
    },
    loading: { type: Boolean, default: false }
  },
  computed: {
    columns () {
      return [
        { key: 'sl_no',         label: 'Sl No',         width: '70px',  sortable: false },
        { key: 'order_number',  label: 'Order Number',  width: '150px' },
        { key: 'contact_name',  label: 'Buyer' },
        { key: 'products',      label: 'Products' },
        { key: 'grand_total',   label: 'Amount',        width: '130px', align: 'right', format: 'currency' },
        { key: 'created_at',    label: 'Date',          width: '140px' },
        { key: 'care_of_name',  label: 'C/O',           width: '160px' },
        { key: 'actions',       label: 'Action',        width: '110px', align: 'center', sortable: false }
      ]
    },
    rangeLabel () {
      const total = Number(this.pagination.total) || 0
      if (!total) return '0 records'
      const page = Number(this.pagination.current_page) || 1
      const perPage = Number(this.pagination.per_page) || 25
      const start = (page - 1) * perPage + 1
      const end = Math.min(page * perPage, total)
      return `${start}–${end} of ${total}`
    }
  },
  methods: {
    slNoFor (index) {
      const page = Number(this.pagination.current_page) || 1
      const perPage = Number(this.pagination.per_page) || 25
      return (page - 1) * perPage + index + 1
    },
    onPageChange (page) {
      if (Number(page) === Number(this.pagination.current_page)) return
      this.$emit('page-change', Number(page))
    },
    formatDate (value) {
      if (!value) return '—'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return value
      const day = String(d.getDate()).padStart(2, '0')
      const month = MONTHS_SHORT[d.getMonth()]
      return `${day} ${month} ${d.getFullYear()}`
    }
  }
}
</script>

<style scoped>
.mvp-tab-panel {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.mvp-tab-panel >>> .base-table tbody td:nth-child(4) {
  white-space: normal;
  min-width: 240px;
}

.mvp-order-no {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.78rem;
  font-weight: 600;
  color: #1f2a44;
  letter-spacing: 0.01em;
}

.mvp-date {
  white-space: nowrap;
  color: #475569;
}

.mvp-care-of-pill {
  display: inline-block;
  padding: 0.18rem 0.6rem;
  border-radius: 999px;
  background: rgba(22, 160, 122, 0.12);
  color: #0e5b46;
  font-weight: 600;
  font-size: 0.75rem;
}

.mvp-row-btn {
  border: none;
  color: #ffffff;
  font-weight: 600;
  border-radius: 8px;
  padding: 0.3rem 0.85rem;
  transition: transform 0.15s ease, filter 0.15s ease;
}

.mvp-row-btn--change {
  background: linear-gradient(135deg, #b45309 0%, #f59e0b 100%);
}

.mvp-row-btn:hover {
  transform: translateY(-1px);
  filter: brightness(1.05);
}

.mvp-pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding-top: 0.25rem;
}

.mvp-pagination-info {
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
}
</style>
